﻿import React, { useState, useEffect } from 'react';
import GlobalDialog from './Modal/GlobalDialog';
import InstrumentSelectBoxTooltip from './InstrumentSelectBoxTooltip';
import InstrumentInformation from './InstrumentInformation';
import { Asset } from '../models/asset';
import { LossOnDryingInstrumentData } from '../models/Instruments/loss-on-drying-instrument-data';
import { BenchSheetTechnologyType } from '../models/enums';
import { FieldUpdate } from '../models/field-update';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { DataTemplates } from '../templates/data-templates';
import { CrudFunctions } from '../utilities/crud-functions';
import LogbookEntryComponent from './Common/LogBookEntryComponent';

import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

export interface Props {
    benchSheetId?: string;
    readonly?: boolean;
}

export default function LossOnDryingInstruments(props: Props) {
    const [instrumentData, setInstrumentData] =
        useState<LossOnDryingInstrumentData>();

    const [balances, setBalances] =
        useState<Asset[]>();

    const [balance, setBalance] =
        useState<Asset>();

    const [ovens, setOvens] =
        useState<Asset[]>();

    const [oven, setOven] =
        useState<Asset>();

    const [selectedLogbookAsset, setSelectedLogbookAsset] =
        useState<Asset>();

    const [logbookEntryDialogVisible, setLogbookEntryDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        if (!dataLoaded)
            getAllData();
    }, [props.benchSheetId]);

    useEffect(() => {
        matchInstruments();
    }, [instrumentData, balances, ovens])

    return (
        <div>
            <div className="lq-header pw-margin-top-05">
                <div className="lq-header-title">Instruments</div>
            </div>

            <div className="pw-form-section">
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Balance</div>
                        <SelectBox
                            id="balanceSelectBox"
                            elementAttr={{ class: "balance-selectbox" }}
                            readOnly={props.readonly}
                            placeholder='Balance'
                            className="asset-selectbox"
                            height={'2.1em'}
                            itemRender={DataTemplates.InstrumentItemRender}
                            dataSource={balances}
                            onValueChanged={onBalanceChanged}
                            valueExpr='assetId'
                            displayExpr={(item) => {
                                return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                            }}
                            value={balance ? balance.assetId : undefined} />
                        <InstrumentSelectBoxTooltip target=".balance-selectbox" instrument={balance} />
                    </div>

                    <div className="pw-row-button-08">
                        <Button text="Logbook" icon="edit" type="success"
                            disabled={!balance}
                            onClick={() => handleLogbookOpenClick(balance)} />
                    </div>

                    <InstrumentInformation instrument={balance} />
                </div>


                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Oven</div>
                        <SelectBox
                            id="ovenSelectBox"
                            elementAttr={{ class: "oven-selectbox" }}
                            readOnly={props.readonly}
                            placeholder='Oven'
                            className="asset-selectbox"
                            height={'2.1em'}
                            itemRender={DataTemplates.InstrumentItemRender}
                            dataSource={ovens}
                            onValueChanged={onOvenChanged}
                            valueExpr='assetId'
                            displayExpr={(item) => {
                                return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                            }}
                            value={oven ? oven.assetId : undefined} />
                        <InstrumentSelectBoxTooltip target=".oven-selectbox" instrument={oven} />
                    </div>

                    <div className="pw-row-button-08">
                        <Button text="Logbook" icon="edit" type="success"
                            disabled={!oven}
                            onClick={() => handleLogbookOpenClick(oven)} />
                    </div>

                    <InstrumentInformation instrument={oven} />
                </div>
            </div>

            {logbookEntryDialogVisible &&
                <DialogPopup visible={logbookEntryDialogVisible}
                    onHiding={hideLogbookEntryDialog}
                    title="Logbook Entry"
                    width='80vw'
                    height='80vh'
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    resizeEnabled={false}>
                    <LogbookEntryComponent asset={selectedLogbookAsset}
                        benchSheetId={props.benchSheetId}
                        onClose={hideLogbookEntryDialog} />
                </DialogPopup>
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div >
    );

    async function onBalanceChanged(e: any) {
        if (!dataLoaded)
            return;

        if (balances && e.value) {
            let selectedBalance = balances.find(b => b.assetId === e.value);

            if (selectedBalance) {
                setBalance(selectedBalance);

                //setBenchSheet({...benchSheet, balance: selectedBalance });

                let fieldUpdate: FieldUpdate = {
                    entity: "LossOnDryingBenchSheets",
                    field: "balanceId",
                    value: e.value,
                    rowId: props.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Balance");
                    });
            }
        }
    }

    async function onOvenChanged(e: any) {
        if (!dataLoaded)
            return;

        if (ovens && e.value) {
            let selectedOven = ovens.find(i => i.assetId === e.value);

            if (selectedOven) {
                setOven(selectedOven);

                let fieldUpdate: FieldUpdate = {
                    entity: "LossOnDryingBenchSheets",
                    field: "ovenId",
                    value: e.value,
                    rowId: props.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Oven");
                    });
            }
        }
    }

    function matchInstruments() {
        if (instrumentData && balances && ovens) {
            let selectedBalance = balances.find(i => i.assetId === instrumentData.balanceId);
            setBalance(selectedBalance);

            let selectedOven = ovens.find(i => i.assetId === instrumentData.ovenId);
            setOven(selectedOven);
        }
    }

    async function getBenchSheetInstrumentData() {
        await CrudFunctions.getData(`benchsheets/lodinstrumentdata/${props.benchSheetId}`)
            .then(data => {
                setInstrumentData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Instrument Data");
            });
    }

    async function getBalances() {
        await CrudFunctions.getData("assets/allbalances")
            .then(data => {
                setBalances(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Balances");
            });
    }

    async function getOvens() {
        await CrudFunctions.getData(`assets/assets?technologyType=${BenchSheetTechnologyType.LossOnDrying}`)
            .then(data => {
                setOvens(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Ovens");
            });
    }

    async function getAllData() {
        await Promise.all([
            getBalances(),
            getOvens(),
            getBenchSheetInstrumentData()
        ])
            .then(() => setDataLoaded(true));
    }

    // PUT functions
    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/lodbenchsheetfield", fieldUpdate);
    }

    function handleLogbookOpenClick(asset) {
        if (!asset) return;
        setSelectedLogbookAsset(asset);
        setLogbookEntryDialogVisible(true);
    }

    function hideLogbookEntryDialog() {
        setLogbookEntryDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}